import { OrderStatus, Prisma, Structure } from "@prisma/client";
import { eventDateValues } from "./events-date.types";
import { eventTypeValues } from "./events.types";
import { onlineOrderDetailsInvoiceArgs } from "./invoice.types";
import {
  computablePricingValues,
  editablePricingValues,
  simplePricingValues,
} from "./pricing.types";
import { simpleStructureValues } from "./structures.types";
import { UserSharedProtected } from "./user.types";
import { EventVisitor } from "./visitors.types";

export const badgeUserSelect = Prisma.validator<Prisma.UserDefaultArgs>()({
  select: {
    id: true,
    picture: true,
    gender: true,
    firstName: true,
    lastName: true,
    email: true,
    connectId: true,
    businessName: true,
    roles: true,
    commercial: {
      select: {
        id: true,
        structureId: true,
      },
    },
    userSharedInformation: true,
  },
});

type User = Prisma.UserGetPayload<typeof badgeUserSelect>;

export const badgeValues = Prisma.validator<Prisma.BadgeDefaultArgs>()({
  include: {
    user: badgeUserSelect,
    badgeType: {
      include: {
        pricing: {
          include: {
            price: true,
          },
        },
        event: eventTypeValues,
      },
    },
    givenToCommercial: true,
    givenToExhibitor: {
      include: {
        structure: simpleStructureValues,
      },
    },
    buyByStructure: simpleStructureValues,
    givenToVisitor: true,
    orderItem: {
      include: {
        order: {
          include: {
            onlineDetails: {
              include: {
                invoice: onlineOrderDetailsInvoiceArgs,
              },
            },
          },
        },
      },
    },
  },
});

export type Badge = Prisma.BadgeGetPayload<typeof badgeValues>;

export const simpleUserBadgesValues = Prisma.validator<Prisma.BadgeDefaultArgs>()({
  select: {
    id: true,
    hmac: true,
    code: true,
    userId: true,
    user: badgeUserSelect,
    orderItem: {
      select: {
        order: {
          select: {
            onlineDetails: {
              select: {
                invoice: onlineOrderDetailsInvoiceArgs,
              },
            },
          },
        },
      },
    },
    badgeTypeId: true,
    badgeType: {
      select: {
        id: true,
        validityDates: true,
        pricing: simplePricingValues,
        name: true,
        event: {
          select: {
            id: true,
            address: true,
            image: true,
            name: true,
            dates: eventDateValues,
          },
        },
        numberEntriesPerDay: true,
        isInfiniteEntries: true,
        customAppearance: {
          include: {
            picture: true,
          },
        },
        customPdfAppearance: {
          include: {
            backgroundPicture: true,
            headerPicture: true,
            textPictures: true,
          },
        },
        eventGates: {
          select: {
            id: true,
          },
        },
      },
    },
  },
});

export type SimpleUserBadge = Prisma.BadgeGetPayload<typeof simpleUserBadgesValues>;

export const simpleBadgesForOrganiser = Prisma.validator<Prisma.BadgeDefaultArgs>()({
  select: {
    id: true,
    userId: true,
    user: badgeUserSelect,
    badgeTypeId: true,
    givenToCommercial: true,
    code: true,
    givenToExhibitor: {
      include: {
        structure: simpleStructureValues,
      },
    },
    buyByStructure: simpleStructureValues,
    isEnabled: true,
    givenToCommercialId: true,
    givenToVisitorId: true,
    invitationStatus: true,
    deliveryMode: true,
    badgeType: {
      select: {
        id: true,
        eventId: true,
        name: true,
        validityDates: true,
      },
    },
  },
});

export type SimpleBadgeForOrganiser = Prisma.BadgeGetPayload<typeof simpleBadgesForOrganiser>;

type SharedProtectedUser = UserSharedProtected<Omit<User, "userSharedInformation">>;

export type BadgeType = Omit<SimpleBadgeForOrganiser, "user" | "givenToVisitor"> & {
  data?: (SharedProtectedUser | EventVisitor) | null;
  structure?: Structure | null;
  user?: SharedProtectedUser | null;
};

export type BadgesGroupedByOwner = {
  userId: string;
  structureId: string;
  lastName: string;
  businessName: string;
  badgeCount: number;
  badgeIds: string[];
  badges: BadgeType[];
};

export const badgeTypeItemValues = Prisma.validator<Prisma.BadgeTypeDefaultArgs>()({
  include: {
    pricing: computablePricingValues,
    badges: true,
  },
});

export type BadgeTypeItemType = Prisma.BadgeTypeGetPayload<typeof badgeTypeItemValues>;

export const badgeTypeValues = Prisma.validator<Prisma.BadgeTypeDefaultArgs>()({
  include: {
    pricing: editablePricingValues,
    badges: {
      include: {
        givenToCommercial: true,
        user: {
          select: {
            commercial: {
              select: {
                id: true,
                structureId: true,
              },
            },
          },
        },
      },
    },
    customAppearance: {
      include: {
        picture: true,
      },
    },
    customPdfAppearance: {
      include: {
        backgroundPicture: true,
        headerPicture: true,
        textPictures: true,
      },
    },
    templatePdf: true,
    eventGates: true,
  },
});

export const badgeTypeValuesWithCount = Prisma.validator<Prisma.BadgeTypeDefaultArgs>()({
  include: {
    pricing: {
      include: {
        price: true,
        degressivePrices: {
          include: { discount: true },
          orderBy: {
            minQuantity: "asc",
          },
        },
        discountCodes: {
          include: {
            discountCode: {
              include: {
                discount: true,
                _count: {
                  select: {
                    orderItemDiscountCodes: {
                      where: {
                        orderItem: {
                          order: {
                            status: {
                              in: [OrderStatus.pending, OrderStatus.success],
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    _count: {
      select: {
        badges: {
          where: {
            OR: [
              { orderItem: null },
              {
                orderItem: {
                  order: {
                    status: {
                      in: [OrderStatus.pending, OrderStatus.success],
                    },
                  },
                },
              },
            ],
          },
        },
      },
    },
    customAppearance: {
      include: {
        picture: true,
      },
    },
    customPdfAppearance: {
      include: {
        backgroundPicture: true,
        headerPicture: true,
        textPictures: true,
      },
    },
    templatePdf: true,
    eventGates: true,
  },
});

export type BadgeTypeType = Prisma.BadgeTypeGetPayload<typeof badgeTypeValues>;

export const downloadBadgesSelect = Prisma.validator<Prisma.BadgeSelect>()({
  id: true,
  code: true,
  hmac: true,
  badgeType: {
    select: {
      name: true,
      validityDates: true,
      pricingId: true,
      pricing: {
        select: {
          isPaid: true,
          price: {
            select: {
              id: true,
              priceTTC: true,
              vatRate: true,
            },
          },
        },
      },
      numberEntriesPerDay: true,
      isInfiniteEntries: true,
      customPdfAppearance: {
        include: {
          backgroundPicture: true,
          headerPicture: true,
          textPictures: true,
        },
      },
      templatePdf: true,
      eventGates: {
        select: {
          id: true,
        },
      },
    },
  },
  user: {
    select: {
      firstName: true,
      lastName: true,
    },
  },
});

export type BadgeWithDownloadInfo = Prisma.BadgeGetPayload<{
  select: typeof downloadBadgesSelect;
}>;

export const scannedBadgeValues = Prisma.validator<Prisma.BadgeDefaultArgs>()({
  include: {
    badgeType: {
      include: {
        event: {
          include: {
            gates: true,
          },
        },
        eventGates: true,
      },
    },
  },
});

export type ScannedBadge = Prisma.BadgeGetPayload<typeof scannedBadgeValues>;

export const commercialBadgeValues = Prisma.validator<Prisma.BadgeDefaultArgs>()({
  select: {
    id: true,
    hmac: true,
    code: true,
    userId: true,
    badgeTypeId: true,
    badgeType: {
      select: {
        id: true,
        validityDates: true,
        pricing: computablePricingValues,
        name: true,
        event: {
          select: {
            id: true,
            name: true,
          },
        },
        numberEntriesPerDay: true,
        isInfiniteEntries: true,
        customAppearance: {
          include: {
            picture: true,
          },
        },
      },
    },
    orderItem: {
      select: {
        order: {
          select: {
            onlineDetails: {
              select: {
                invoice: onlineOrderDetailsInvoiceArgs,
              },
            },
          },
        },
      },
    },
  },
});

export type CommercialBadge = Prisma.BadgeGetPayload<typeof commercialBadgeValues>;
